
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { Family, Student } from '@/types/models';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { Loader2, Plus } from 'lucide-react';
import { mapToFamily } from '@/utils/supabaseTypeMappers';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FamilyService } from '@/services/familyService';

interface ConnectToFamilyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  student: Student;
  onStudentConnected: (updatedStudent: Student) => void;
}

const ConnectToFamilyDialog: React.FC<ConnectToFamilyDialogProps> = ({
  open,
  onOpenChange,
  student,
  onStudentConnected
}) => {
  const { organizationId } = useAuth();
  const [families, setFamilies] = useState<Family[]>([]);
  const [selectedFamilyId, setSelectedFamilyId] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFetchingFamilies, setIsFetchingFamilies] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>('existing');
  const [newFamilyName, setNewFamilyName] = useState<string>('');

  useEffect(() => {
    if (open && organizationId) {
      fetchFamilies();
    }
  }, [open, organizationId]);

  useEffect(() => {
    // Reset the form when dialog opens/closes
    if (!open) {
      setSelectedFamilyId('');
      setNewFamilyName('');
      setActiveTab('existing');
    }
  }, [open]);

  const fetchFamilies = async () => {
    if (!organizationId) return;
    
    setIsFetchingFamilies(true);
    try {
      const { data, error } = await supabase
        .from('families')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('is_active', true)
        .order('name');
        
      if (error) throw error;
      
      // Use the mapToFamily function to correctly convert the data to Family[] type
      const mappedFamilies = data.map(family => mapToFamily(family));
      setFamilies(mappedFamilies);
    } catch (error) {
      console.error('Error fetching families:', error);
      toast.error('Failed to load families');
    } finally {
      setIsFetchingFamilies(false);
    }
  };

  const handleConnectToExistingFamily = async () => {
    if (!selectedFamilyId) {
      toast.error('Please select a family');
      return;
    }
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('students')
        .update({ family_id: selectedFamilyId })
        .eq('id', student.id)
        .select()
        .single();
        
      if (error) throw error;
      
      toast.success('Student connected to family successfully');
      onStudentConnected(data as Student);
      onOpenChange(false);
    } catch (error) {
      console.error('Error connecting student to family:', error);
      toast.error('Failed to connect student to family');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateAndConnectFamily = async () => {
    if (!newFamilyName.trim()) {
      toast.error('Please enter a family name');
      return;
    }

    if (!organizationId) {
      toast.error('Organization ID not found');
      return;
    }
    
    setIsLoading(true);
    try {
      // Step 1: Create a new family
      const newFamily = await FamilyService.createFamily({
        name: newFamilyName.trim(),
        organization_id: organizationId,
        billing_status: 'current',
        is_active: true
      });
      
      if (!newFamily) {
        throw new Error("Failed to create new family");
      }

      // Step 2: Connect the student to the newly created family
      const { data, error } = await supabase
        .from('students')
        .update({ family_id: newFamily.id })
        .eq('id', student.id)
        .select()
        .single();
        
      if (error) throw error;
      
      toast.success('New family created and student connected successfully');
      onStudentConnected(data as Student);
      onOpenChange(false);
    } catch (error) {
      console.error('Error creating family and connecting student:', error);
      toast.error('Failed to create family and connect student');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = () => {
    if (activeTab === 'existing') {
      handleConnectToExistingFamily();
    } else {
      handleCreateAndConnectFamily();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Connect Student to Family</DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="existing">Existing Family</TabsTrigger>
            <TabsTrigger value="new">New Family</TabsTrigger>
          </TabsList>
          
          <div className="space-y-4 py-4">
            <TabsContent value="existing" className="space-y-2">
              <Label htmlFor="family">Select Family</Label>
              <Select
                disabled={isFetchingFamilies}
                onValueChange={setSelectedFamilyId}
                value={selectedFamilyId}
              >
                <SelectTrigger id="family" className="w-full">
                  <SelectValue placeholder="Select a family" />
                </SelectTrigger>
                <SelectContent>
                  {families.length > 0 ? (
                    families.map((family) => (
                      <SelectItem key={family.id} value={family.id}>
                        {family.name}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem disabled value="loading">
                      {isFetchingFamilies ? 'Loading families...' : 'No families found'}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </TabsContent>
            
            <TabsContent value="new" className="space-y-2">
              <Label htmlFor="familyName">Family Name</Label>
              <Input 
                id="familyName" 
                placeholder="Enter new family name" 
                value={newFamilyName}
                onChange={(e) => setNewFamilyName(e.target.value)}
              />
            </TabsContent>
          </div>
        </Tabs>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={isLoading || (activeTab === 'existing' && !selectedFamilyId) || (activeTab === 'new' && !newFamilyName.trim())}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {activeTab === 'existing' ? 'Connect to Family' : 'Create & Connect Family'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConnectToFamilyDialog;
